import {
    ActionConstructor,
    GetData,
    Observer,
    State,
} from '@raskrask-dk/bookingflow-state-machine'
import type { Action, StateName } from '@raskrask-dk/bookingflow-state-machine'
import { useEffect, useState } from 'react'
import { useBFSM } from '../../contexts/bfsmContext'
import {
    ActionData,
    ActionOfState,
} from '@raskrask-dk/bookingflow-state-machine'

// type BFSMValue<k extends keyof GetData> = k extends undefined ? undefined : GetData[k]

/**
 * Hook which
 *
 * @param k name of the property which is returned from bfsm.getData
 * @param action to call when new setValue is set
 * @returns useState like  for bfsm values
 */
const useBFSMValue = <
    S extends State<any, StateName>,
    GAction extends Action<string, any>,
    Key extends keyof GetData<S>
>(
    key: Key,
    action?: ActionConstructor<ActionOfState<S>, S, ActionData<GAction>>
): [ActionData<GAction>, (data: ActionData<GAction> | undefined) => void] => {
    const [bfsm] = useBFSM()
    const [value, setValue] = useState(bfsm.getData()[key])

    const doAction = (data: any | undefined) => {
        if (action) {
            bfsm.doAction(action as any, data!)
        } else
            throw new Error(
                `useBFSMValue(${String(
                    key
                )}): Provide an action to use the setter`
            )
    }

    const observer: Observer = {
        id: `${String(key)}` + Math.floor(Math.random() * 10000).toString(), // avoids collision (multiple could listen to same key)
        notify: () => setValue(bfsm.getData()[key]),
    }

    const subscribe = () => bfsm.attach(observer)
    const unsubscribe = () => bfsm.detach(observer.id)

    useEffect(() => {
        subscribe()
        return () => unsubscribe()
    }, [])

    return [value, doAction]
}

export { useBFSMValue }
